import { ESuggestedProductViewId } from 'core/model/enums/catalog.enum';
import { EProductType } from 'core/model/enums/product.enum';
import { ECartActions, ECartError } from 'core/model/enums/shopping-cart.enum';
import { shoppingCartService } from 'core/services/shopping-cart/shopping-cart.service';
import type { TSliceExtraReducer } from 'core/store';
import { refreshCheckoutDetailAction } from 'core/store/checkout';
import { getCustomerIdFromCheckout } from 'core/store/checkout/checkout-state.utils';
import { createAppAsyncThunk } from 'core/store/store.utils';
import { getSuggestedProducts } from 'core/store/suggested-products';
import { getCustomerIdByProductSource } from '../cart-state.utils';
import { CART_ERRORS_INITIAL_STATE, EMPTY_SHOPPING_CART_GROUPS, SLICE_NAMESPACE } from '../constants';
import { ICartState } from '../types';

export const removeProductFromCartAction = createAppAsyncThunk(
  `${SLICE_NAMESPACE}/removeProductFromCartAction`,
  async (
    payload: { sku: string; sourceId: EProductType; shouldRefreshCheckoutDetails: boolean },
    { getState, dispatch },
  ) => {
    const { checkout, auth } = getState();
    const checkoutCustomerId = getCustomerIdFromCheckout(checkout);
    const customerIdByProductSource = getCustomerIdByProductSource(auth.customer, payload.sourceId);
    const customerId = customerIdByProductSource || checkoutCustomerId;
    await shoppingCartService.deleteProductShoppingCart(payload.sku, customerId);
    if (payload.shouldRefreshCheckoutDetails) dispatch(refreshCheckoutDetailAction());
    dispatch(getSuggestedProducts({ sectionId: ESuggestedProductViewId.shoppingCart }));
    try {
      const result = await shoppingCartService.getShoppingCart(customerId, { cartAction: ECartActions.REFRESH });
      return result;
    } catch {
      throw new Error(ECartError.SHOW_ERROR_AND_KEEP_CART_OPEN);
    }
  },
);

export const removeProductFromCartReducer: TSliceExtraReducer<ICartState> = builder => {
  builder
    .addCase(removeProductFromCartAction.pending, (state, { meta }) => {
      state.summaryLoading = true;
      state.productsLoading = [...state.productsLoading, meta.arg.sku];
      state.success = false;
      state.errors = CART_ERRORS_INITIAL_STATE;
    })
    .addCase(removeProductFromCartAction.fulfilled, (state, { payload, meta }) => {
      state.itemsGroups = payload.itemsGroups || EMPTY_SHOPPING_CART_GROUPS;
      state.deletedProducts = payload.deletedProducts;
      state.amounts = payload.amounts;
      state.productsLoading = state.productsLoading.filter(pc => pc !== meta.arg.sku);
      state.summaryLoading = false;
      state.errors = { userValidation: payload.errorType, getData: null };
      state.success = true;
      state.isValid = payload.isValid;
    })
    .addCase(removeProductFromCartAction.rejected, (state, { error, meta }) => {
      state.productsLoading = state.productsLoading.filter(pc => pc !== meta.arg.sku);
      state.summaryLoading = false;
      if (error?.message) state.errors.getData = error.message;
      state.success = false;
    });

  return builder;
};
